"use client";

import React, { useMemo, useState } from "react";
import Link from "next/link";

function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

interface BlogItem {
  title: string;
  excerpt: string;
  category: string;
  date: string;
}

interface BlogsPageClientProps {
  blogs: BlogItem[];
}

function normalizeDate(value?: string): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return isNaN(t) ? 0 : t;
}

const PAGE_SIZE = 9;

const BlogsPageClient: React.FC<BlogsPageClientProps> = ({ blogs }) => {
  const [page, setPage] = useState(1);

  const sortedBlogs = useMemo(() => {
    if (!blogs?.length) return [] as BlogItem[];
    return [...blogs].sort((a, b) => normalizeDate(b.date) - normalizeDate(a.date));
  }, [blogs]);

  const totalPages = Math.max(1, Math.ceil(sortedBlogs.length / PAGE_SIZE));

  const currentPage = Math.min(Math.max(page, 1), totalPages);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedBlogs.slice(start, start + PAGE_SIZE);
  }, [sortedBlogs, currentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  return (
    <main className="min-h-screen bg-[#050816] text-white pt-24 pb-20">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-400/80 mb-3">
            CasinoB8 Blog
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4">
            All Casino Guides & Insights
          </h1>
          <p className="text-sm md:text-base text-slate-300 max-w-2xl mx-auto">
            Browse every blog post and strategy guide published for CasinoB8.
            Use the pagination to explore more in-depth content.
          </p>
        </header>

        {pageItems.length === 0 ? (
          <p className="text-center text-slate-400">
            No blogs are available yet. Please check back soon.
          </p>
        ) : (
          <div className="grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">
            {pageItems.map((post, index) => {
              const globalIndex = (currentPage - 1) * PAGE_SIZE + index;

              return (
                <Link
                  key={`${post.title}-${index}`}
                  href={`/blog-details?id=${globalIndex}`}
                  className="relative flex flex-col h-full rounded-2xl bg-gradient-to-b from-[#151531] to-[#050816] border border-[#26264a] p-5 shadow-lg shadow-black/40 hover:scale-[1.02] transition-transform"
                >
                  <header className="mb-3">
                    <p className="text-[11px] text-sky-300 uppercase tracking-wide mb-1">
                      {post.category}
                    </p>
                    <h2 className="text-lg md:text-xl font-semibold leading-snug mb-1">
                      {post.title}
                    </h2>
                    {post.date && (
                      <p className="text-[11px] text-slate-400">{post.date}</p>
                    )}
                  </header>
                  <p className="text-sm text-slate-300 leading-relaxed flex-1 mb-3">
                    {post.excerpt}
                  </p>
                  <footer className="mt-auto pt-2 text-xs text-sky-400 flex items-center justify-between">
                    <span>CasinoB8 Blog</span>
                  </footer>
                </Link>
              );
            })}
          </div>
        )}

        {sortedBlogs.length > PAGE_SIZE && (
          <nav
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
            aria-label="Blog pagination"
          >
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-full text-xs md:text-sm border border-gray-600 text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:border-yellow-400 hover:text-yellow-300 transition-colors"
            >
              Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }).map((_, idx) => {
                const pageNum = idx + 1;
                const isActive = pageNum === currentPage;
                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded-full text-xs md:text-sm flex items-center justify-center border transition-colors ${
                      isActive
                        ? "bg-yellow-400 text-black border-yellow-400"
                        : "border-gray-600 text-gray-200 hover:border-yellow-400 hover:text-yellow-300"
                    }`}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-full text-xs md:text-sm border border-gray-600 text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:border-yellow-400 hover:text-yellow-300 transition-colors"
            >
              Next
            </button>
          </nav>
        )}

        <div className="mt-10 text-center text-slate-400 text-xs md:text-sm">
          <p>
            Looking for more? Return to the{" "}
            <Link
              href="/"
              className="text-cyan-300 hover:text-yellow-300 underline underline-offset-2"
            >
              CasinoB8 homepage
            </Link>{" "}
            for featured content.
          </p>
        </div>
      </section>
    </main>
  );
};

export default BlogsPageClient;
